import { FileSpreadsheet, Users, Wallet } from 'lucide-react';
import GraficoLinha from '@/components/GraficoLinha';
import { SITUACAO } from '@/features/folha/tipos';
import { Cartao, CartaoIndicador, ErroDoBloco, Esqueleto, EsqueletoIndicador, type Variacao } from './componentes';
import { reais, reaisDoEixo, variacaoComSinal } from './formatos';
import type { ResumoFolha } from './consultas';

/** Na folha a variação só informa: nem subir nem descer é boa notícia por si. */
function informativa(atual: number, anterior: number | undefined): Variacao | null {
  if (!anterior) return null;
  const fracao = atual / anterior - 1;

  return { texto: variacaoComSinal(fracao), direcao: fracao > 0 ? 'sobe' : fracao < 0 ? 'desce' : 'estavel', leitura: 'neutra' };
}

export function IndicadorFolha({ dados, carregando }: { dados?: ResumoFolha; carregando: boolean }) {
  if (carregando || !dados) return <EsqueletoIndicador />;

  const { atual } = dados;
  if (!atual) {
    return <CartaoIndicador icone={Wallet} rotulo="Folha do mês" valor="—" detalhe="Nenhuma competência importada" to="/folha" />;
  }

  return (
    <CartaoIndicador
      icone={Wallet}
      rotulo={`Folha de ${atual.rotulo}`}
      valor={reais.format(atual.liquido)}
      variacao={informativa(atual.liquido, atual.anterior?.liquido)}
      detalhe={atual.anterior ? `líquido, sobre ${atual.anterior.rotulo}` : `líquido · ${atual.funcionarios} funcionários`}
      to={`/folha/${atual.id}`}
    />
  );
}

/** A competência mais recente com o que ela custou, e a linha dos meses importados. */
export function CartaoFolha({ dados, carregando, erro }: { dados?: ResumoFolha; carregando: boolean; erro: boolean }) {
  const atual = dados?.atual;

  return (
    <Cartao
      titulo="Folha de pagamento"
      subtitulo="A competência mais recente e a evolução dos proventos."
      atalho={{ rotulo: 'Ver folhas', to: '/folha' }}
    >
      {erro ? (
        <ErroDoBloco oQue="a folha" />
      ) : carregando || !dados ? (
        <div className="grid gap-md lg:grid-cols-[280px_1fr]">
          <Esqueleto className="h-[160px] w-full" />
          <Esqueleto className="h-[160px] w-full" />
        </div>
      ) : !atual ? (
        <div className="flex min-h-[120px] flex-col items-center justify-center rounded-md border border-dashed border-border px-md py-lg text-center">
          <FileSpreadsheet size={22} className="text-muted" />
          <p className="mt-sm text-[13px] text-muted">Quando a primeira folha for importada, o resumo do mês aparece aqui.</p>
        </div>
      ) : (
        <div className="grid gap-lg lg:grid-cols-[280px_1fr]">
          <div>
            <div className="flex items-center justify-between gap-sm">
              <p className="text-[18px] font-bold text-ink">{atual.rotulo}</p>
              <span className={`rounded-full px-sm py-[2px] text-[11px] font-bold whitespace-nowrap ${SITUACAO[atual.status].classe}`}>
                {SITUACAO[atual.status].rotulo}
              </span>
            </div>
            <dl className="mt-md grid grid-cols-2 gap-x-md gap-y-sm text-[13px]">
              <Numero rotulo="Proventos" valor={reais.format(atual.proventos)} variacao={informativa(atual.proventos, atual.anterior?.proventos)} />
              <Numero rotulo="Líquido" valor={reais.format(atual.liquido)} variacao={informativa(atual.liquido, atual.anterior?.liquido)} />
              <Numero rotulo="Descontos" valor={reais.format(atual.descontos)} />
              <Numero
                rotulo="Funcionários"
                valor={String(atual.funcionarios)}
                variacao={informativa(atual.funcionarios, atual.anterior?.funcionarios)}
                icone
              />
            </dl>
            {atual.anterior && <p className="mt-sm text-[12px] text-muted">Variação sobre {atual.anterior.rotulo}.</p>}
          </div>

          <div className="min-w-0">
            <p className="mb-xs text-[12px] text-muted">Proventos por competência</p>
            {dados.serie.length < 2 ? (
              <div className="flex h-[170px] items-center justify-center rounded-md border border-dashed border-border px-md text-center text-[13px] text-muted">
                A linha aparece a partir da segunda competência importada.
              </div>
            ) : (
              <GraficoLinha
                pontos={dados.serie.map((f) => ({ rotulo: f.rotulo, valor: f.proventos }))}
                formatar={(v) => reaisDoEixo.format(v)}
                altura={170}
                larguraMinima={260}
                descricao="Proventos da folha por competência"
              />
            )}
          </div>
        </div>
      )}
    </Cartao>
  );
}

function Numero({ rotulo, valor, variacao, icone }: { rotulo: string; valor: string; variacao?: Variacao | null; icone?: boolean }) {
  return (
    <div>
      <dt className="flex items-center gap-xs text-muted">
        {icone && <Users size={13} />}
        {rotulo}
      </dt>
      <dd className="font-bold text-ink tabular-nums">
        {valor}
        {variacao && <span className="ml-xs text-[11px] font-normal text-muted">{variacao.texto}</span>}
      </dd>
    </div>
  );
}
